import {
  Input,
  Tab,
  TabList,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { SearchRegular } from '@fluentui/react-icons';
import { useMemo, useState } from 'react';
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';

import type { AdminUser } from '@/api/types';
import {
  DataTable,
  EmptyState,
  type DataTableColumn,
  ErrorState,
  FreshnessChip,
  KeyValue,
  KpiTile,
  LoadingState,
  SectionCard,
} from '@/design-system';
import type { DirectoryUserWithRoles } from '@/models';
import { spacing } from '@/theme/tokens';

import { useFacility, useFacilityUsers } from '../api';
import { IdentifierList } from '../components/IdentifierList';
import { identifierDisplayValue } from '../components/identifierDisplayValue';
import { StatusBadge } from '../components/StatusBadge';

import { buildPathWithMergedSearch, normalizeSearchParam } from '../navigation';

import { formatAddress, formatDateTime, maxRolledUpAtMs } from './detailPageUtils';

type FacilityDetailTab = 'overview' | 'users';
const TAB_VALUES: readonly FacilityDetailTab[] = ['overview', 'users'];

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.l,
  },
  breadcrumb: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.xs,
    flexWrap: 'wrap',
  },
  breadcrumbLink: {
    color: tokens.colorBrandForegroundLink,
    textDecoration: 'none',
    ':hover': {
      textDecoration: 'underline',
    },
    ':focus-visible': {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: '2px',
      borderRadius: tokens.borderRadiusSmall,
    },
  },
  breadcrumbCurrent: {
    color: tokens.colorNeutralForeground3,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: spacing.l,
    flexWrap: 'wrap',
    paddingBottom: spacing.l,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  headerText: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.s,
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.s,
    flexWrap: 'wrap',
  },
  title: {
    color: tokens.colorNeutralForeground1,
  },
  subtitle: {
    color: tokens.colorNeutralForeground3,
  },
  orgLink: {
    color: tokens.colorBrandForegroundLink,
    textDecoration: 'none',
    ':hover': {
      textDecoration: 'underline',
    },
    ':focus-visible': {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: '2px',
      borderRadius: tokens.borderRadiusSmall,
    },
  },
  kpiGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: spacing.m,
  },
  tabPanel: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.l,
  },
  overviewGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: spacing.l,
  },
  kvGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: spacing.m,
  },

  searchInput: {
    maxWidth: '320px',
  },
  sectionText: {
    color: tokens.colorNeutralForeground3,
  },
});

function isFacilityDetailTab(value: unknown): value is FacilityDetailTab {
  return typeof value === 'string' && (TAB_VALUES as readonly string[]).includes(value);
}

function UsersTable({ users, hasQuery, endpointUnavailable, partial }: { users: readonly DirectoryUserWithRoles[]; hasQuery: boolean; endpointUnavailable: boolean; partial: boolean }) {
  if (endpointUnavailable) return <EmptyState title="Users unavailable" description="This environment has not shipped the facility users endpoint yet." />;
  const columns: readonly DataTableColumn<DirectoryUserWithRoles>[] = [
    { id: 'name', header: 'Name', getValue: (item) => item.displayName, renderCell: (item) => item.displayName, sortable: !partial },
    { id: 'email', header: 'Email', getValue: (item) => item.emailAddress, renderCell: (item) => item.emailAddress, width: 280, sortable: !partial },
    { id: 'status', header: 'Status', getValue: (item) => item.status, renderCell: (item) => <StatusBadge status={item.status} />, sortable: !partial },
    { id: 'roles', header: 'Roles', getValue: (item) => identifierDisplayValue(item.roleReferences, item.roles), renderCell: (item) => <IdentifierList references={item.roleReferences} fallbackIds={item.roles} />, width: 260, sortable: !partial },
  ];
  return <DataTable ariaLabel="Facility users" columns={columns} getRowId={(item) => item.userId} items={users} tableId="facility-users" sortDescription={partial ? 'User order follows the server order because this is a partial result.' : undefined} emptyState={<EmptyState title={hasQuery ? 'No users match the filter' : 'No users found'} description={hasQuery ? 'Try a shorter or different search query.' : "This facility doesn't have any users to show right now."} />} />;
}

function AdminsTable({ admins }: { admins: readonly AdminUser[] }) {
  const columns: readonly DataTableColumn<AdminUser>[] = [
    { id: 'name', header: 'Name', getValue: (item) => item.displayName, renderCell: (item) => item.displayName },
    { id: 'email', header: 'Email', getValue: (item) => item.email, renderCell: (item) => item.email, width: 280 },
    { id: 'status', header: 'Status', getValue: (item) => item.status, renderCell: (item) => <StatusBadge status={item.status} /> },
    { id: 'lastSignIn', header: 'Last sign-in', getValue: (item) => (item.lastSignInEpochMs ?? 0) > 0 ? item.lastSignInEpochMs : null, renderCell: (item) => formatDateTime(item.lastSignInEpochMs) },
  ];
  return <DataTable ariaLabel="Facility admins" columns={columns} getRowId={(item) => item.userId} items={admins} tableId="facility-admins" emptyState={<EmptyState title="No admins found" description="This facility doesn't have any admin assignments yet." />} />;
}

function FacilityUsersPanel({
  users,
  isLoading,
  errorMessage,
  onRetry,
  userSearch,
  onUserSearchChange,
  isCapped,
  endpointUnavailable,
}: {
  users: readonly DirectoryUserWithRoles[];
  isLoading: boolean;
  errorMessage?: string;
  onRetry: () => void;
  userSearch: string;
  onUserSearchChange: (value: string) => void;
  isCapped: boolean;
  endpointUnavailable: boolean;
}) {
  const classes = useStyles();

  return (
    <SectionCard
      title="Users"
      description="Users assigned to this facility."
      trailing={
        <Input
          className={classes.searchInput}
          contentBefore={<SearchRegular />}
          placeholder="Search users"
          value={userSearch}
          onChange={(_, data) => {
            onUserSearchChange(data.value);
          }}
          aria-label="Search facility users"
        />
      }
    >
      {isLoading ? <LoadingState label="Loading facility users…" /> : null}
      {!isLoading && errorMessage ? (
        <ErrorState
          title="Couldn't load facility users"
          description={errorMessage}
          retry={onRetry}
        />
      ) : null}
      {!isLoading && !errorMessage ? (
        endpointUnavailable
          ? <UsersTable users={users} hasQuery={userSearch.trim().length > 0} endpointUnavailable={endpointUnavailable} partial={isCapped} />
          : <>
              <Text size={200} className={classes.sectionText}>
                {isCapped
                  ? `Showing the first ${users.length.toLocaleString()} users. Refine the search to narrow the list.`
                  : `Showing ${users.length.toLocaleString()} users`}
              </Text>
              <UsersTable users={users} hasQuery={userSearch.trim().length > 0} endpointUnavailable={endpointUnavailable} partial={isCapped} />
            </>
      ) : null}
    </SectionCard>
  );
}

export function FacilityDetailPage() {
  const classes = useStyles();
  const location = useLocation();
  const { facilityId: facilityIdParam } = useParams<{ facilityId: string }>();
  const [searchParams] = useSearchParams();
  const facilityId = facilityIdParam ?? null;
  const requestedOrgId = normalizeSearchParam(searchParams.get('orgId'));
  const [selectedTab, setSelectedTab] = useState<FacilityDetailTab>('overview');
  const [userSearch, setUserSearch] = useState('');

  const facilityQuery = useFacility(facilityId, requestedOrgId);
  const effectiveOrgId = facilityQuery.data?.organizationId ?? requestedOrgId ?? null;
  const usersQuery = useFacilityUsers(facilityId, effectiveOrgId, userSearch, selectedTab === 'users');
  const users = useMemo(() => usersQuery.data?.items ?? [], [usersQuery.data]);
  const usersLoading = usersQuery.isLoading || (selectedTab === 'users' && effectiveOrgId === null && facilityQuery.isLoading);

  if (facilityQuery.isLoading) {
    return <LoadingState label="Loading facility details…" />;
  }

  if (facilityQuery.error) {
    return (
      <ErrorState
        title="Couldn't load facility"
        description={facilityQuery.error.message}
        retry={() => {
          void facilityQuery.refetch();
        }}
      />
    );
  }

  if (!facilityQuery.data) {
    return <ErrorState title="Facility not found" description="Try returning to the business directory and selecting the facility again." />;
  }

  const detail = facilityQuery.data;
  // dataAsOfMs is the age of the oldest input behind the response, so the chip states how
  // old the numbers are rather than when the page was fetched.
  const freshnessMs = detail.dataAsOfMs || maxRolledUpAtMs(detail.countsRolledUpAtMs);
  const orgHref = buildPathWithMergedSearch(
    `/business/organizations/${encodeURIComponent(detail.organizationId)}`,
    location.search,
    { orgId: null },
  );

  return (
    <div className={classes.root}>
      <nav className={classes.breadcrumb} aria-label="Breadcrumb">
        <Link
          to={buildPathWithMergedSearch('/business', location.search, { orgId: null })}
          className={classes.breadcrumbLink}
        >
          Business
        </Link>
        <Text size={200} className={classes.breadcrumbCurrent} aria-hidden="true">›</Text>
        <Text size={200} className={classes.breadcrumbCurrent}>Organizations</Text>
        <Text size={200} className={classes.breadcrumbCurrent} aria-hidden="true">›</Text>
        <Link to={orgHref} className={classes.breadcrumbLink}>{detail.organizationDisplayName}</Link>
        <Text size={200} className={classes.breadcrumbCurrent} aria-hidden="true">›</Text>
        <Text size={200} className={classes.breadcrumbCurrent} aria-current="page">{detail.displayName}</Text>
      </nav>

      <div className={classes.header}>
        <div className={classes.headerText}>
          <div className={classes.titleRow}>
            <Text size={700} weight="semibold" className={classes.title}>
              {detail.displayName}
            </Text>
          </div>
          <Text size={300} className={classes.subtitle}>
            {formatAddress(detail.addresses)}
          </Text>
          <Text size={300} className={classes.subtitle}>
            Organization:{' '}
            <Link className={classes.orgLink} to={orgHref}>
              {detail.organizationDisplayName}
            </Link>
          </Text>
        </div>
        {freshnessMs ? (
          <FreshnessChip
            asOfMs={freshnessMs}
            isRefreshing={facilityQuery.isFetching || usersQuery.isFetching}
            onRefresh={() => {
              void facilityQuery.refetch();
              if (selectedTab === 'users') {
                void usersQuery.refetch();
              }
            }}
          />
        ) : null}
      </div>

      <div className={classes.kpiGrid}>
        <KpiTile label="Total users" value={detail.counts.usersTotal} unit="count" />
        <KpiTile
          label="Active users"
          value={detail.counts.usersActive}
          unit="count"
        />
        <KpiTile label="Docs created (last 7 days)" value={detail.counts.docsCreatedLast7d} unit="count" />
        <KpiTile label="Docs signed (last 7 days)" value={detail.counts.docsSignedLast7d} unit="count" />
        <SectionCard title="Last activity">
          <Text size={600} weight="semibold">{formatDateTime(detail.counts.lastActivityEpochMs)}</Text>
        </SectionCard>
      </div>

      <TabList
        selectedValue={selectedTab}
        onTabSelect={(_, data) => {
          if (isFacilityDetailTab(data.value)) {
            setSelectedTab(data.value);
          }
        }}
        aria-label="Facility detail tabs"
      >
        <Tab value="overview">Overview</Tab>
        <Tab value="users">Users</Tab>
      </TabList>

      {selectedTab === 'overview' ? (
        <div className={classes.tabPanel}>
          <div className={classes.overviewGrid}>
            <SectionCard title="Contacts">
              <div className={classes.kvGrid}>
                <KeyValue label="Primary contact">{detail.contact.name ?? '—'}</KeyValue>
                <KeyValue label="Email">{detail.contact.email ?? '—'}</KeyValue>
                <KeyValue label="Phone">{detail.contact.phone ?? '—'}</KeyValue>
                <KeyValue label="Address">{formatAddress(detail.addresses)}</KeyValue>
              </div>
            </SectionCard>
            <SectionCard title="Key dates">
              <div className={classes.kvGrid}>
                <KeyValue label="Created">{formatDateTime(detail.createdAtEpochMs)}</KeyValue>
                <KeyValue label="Last activity">{formatDateTime(detail.counts.lastActivityEpochMs)}</KeyValue>
                <KeyValue label="License type">{detail.license?.type ?? '—'}</KeyValue>
                <KeyValue label="License expires">{formatDateTime(detail.license?.validThroughEpochMs)}</KeyValue>
              </div>
            </SectionCard>
          </div>
          <SectionCard title="Admins" description="Facility-level admins and recent sign-in activity.">
            <AdminsTable admins={detail.admins} />
          </SectionCard>
        </div>
      ) : null}

      {selectedTab === 'users' ? (
        <div className={classes.tabPanel}>
          <FacilityUsersPanel
            users={users}
            isLoading={usersLoading}
            errorMessage={usersQuery.error?.message}
            onRetry={() => {
              void usersQuery.refetch();
            }}
            userSearch={userSearch}
            onUserSearchChange={setUserSearch}
            isCapped={usersQuery.data?.isCapped ?? false}
            endpointUnavailable={usersQuery.data?.endpointUnavailable ?? false}
          />
        </div>
      ) : null}
    </div>
  );
}
